// src/pages/business/ActivateTagsPage.tsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { ArrowLeft, Upload, Scan, CheckCircle, AlertCircle, Download, Camera, Wifi } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BusinessApiService from "../../services/BusinessApiService";
import { getCurrentBusinessId } from "../../services/api";

// ✅ resolveTagId — extracts clean TagID/UID from any URL format
// Handles: GS1 (/01/GTIN/21/SN), NFC mirror (?uid=), plain SN
function resolveTagId(input: string): string {
  if (!input) return input;
  const raw = input.trim();
  if (!raw.includes("://") && !raw.startsWith("www.")) {
    return raw.replace(/^UID:/i, "").toUpperCase();
  }
  try {
    const url = new URL(raw.startsWith("http") ? raw : "https://" + raw);
    const path = url.pathname;
    const uidParam = url.searchParams.get("uid") ||
                     url.searchParams.get("UID") ||
                     url.searchParams.get("tagId");
    // GS1 + NFC mirror: /01/GTIN/21/?uid=XXXX
    const isGS1 = /\/01\/[\d]+\/21/.test(path);
    if (isGS1 && uidParam) return uidParam.replace(/^UID:/i, "").toUpperCase();
    // GS1 clean: /01/GTIN/21/SN
    const gs1 = path.match(/\/01\/[\d]+\/21\/([^\/\?]+)/);
    if (gs1) return gs1[1].toUpperCase();
    // Any ?uid= param
    if (uidParam) return uidParam.replace(/^UID:/i, "").toUpperCase();
    // /verify/ or /scan/ path
    const scan = path.match(/\/(?:scan|verify)\/([^\/\?]+)/);
    if (scan) return scan[1].toUpperCase();
    // last segment
    const parts = path.split("/").filter(Boolean);
    return (parts[parts.length - 1] || raw).toUpperCase();
  } catch {
    return raw.toUpperCase();
  }
}

/** ------------------------- Types ------------------------- **/
interface ActivationResult {
  success: boolean;
  message: string;
  activated?: number;
  failed?: number;
  details?: string[];
  errors?: string[];
  summary?: {
    tagsActivated?: number;
    tagsNotFound?: number;
    successRate?: string;
    duplicatesRemoved?: number;
  };
  notFound?: {
    count: number;
    sample: string[];
    reason: string;
  };
}

const API_BASE = process.env.REACT_APP_API_URL || `${process.env.REACT_APP_API_URL || "http://localhost:3000/api"}`;

/** ------------------------- Component ------------------------- **/
export default function ActivateTagsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Context (prefill from query, fallback to storage/empty)
  const [businessId, setBusinessId] = useState<string>("");
  const [productId, setProductId] = useState<string>("");
  const [batchId, setBatchId] = useState<string>("");
  const [storeId, setStoreId] = useState<string>("");

  // Scan
  const [tagId, setTagId] = useState("");

  // CSV
  const [csvData, setCsvData] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);

  // UI
  const [loading, setLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [activationResults, setActivationResults] = useState<ActivationResult | null>(null);

  // ✅ NFC + QR Scan state
  const [nfcSupported, setNfcSupported] = useState(false);
  const [nfcScanning, setNfcScanning] = useState(false);
  const [nfcStatus, setNfcStatus] = useState("");
  const [scanMode, setScanMode] = useState<"manual" | "nfc" | "qr">("manual");
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // ✅ GPS Status
  const [gpsStatus, setGpsStatus] = useState<"checking" | "ready" | "denied" | "unavailable">("checking");
  const [gpsCoords, setGpsCoords] = useState<{ latitude: number; longitude: number; accuracy?: number } | null>(null);

  // derive if context looks complete
  const contextComplete = useMemo(
    () => !!(businessId && productId && batchId && storeId),
    [businessId, productId, batchId, storeId]
  );

  // Extract batch ID from URL path
  const getBatchIdFromPath = useCallback(() => {
    const pathSegments = window.location.pathname.split('/');
    const batchesIndex = pathSegments.findIndex(segment => segment === 'batches');
    if (batchesIndex !== -1 && pathSegments[batchesIndex + 1]) {
      return pathSegments[batchesIndex + 1];
    }
    return '';
  }, []);

  // Initialize from query params and storage
  useEffect(() => {
    const qBiz = searchParams.get("businessId")?.trim() || "";
    const qProd = searchParams.get("productId")?.trim() || "";
    const qBatch = searchParams.get("batchId")?.trim() || "";
    const qStore = searchParams.get("storeId")?.trim() || "";
    const pathBatchId = getBatchIdFromPath(); // Get from URL path

    setBusinessId(qBiz || getCurrentBusinessId() || "");
    setProductId(qProd);
    setBatchId(pathBatchId || qBatch); // Prioritize path over query param
    setStoreId(qStore);
  }, [searchParams, getBatchIdFromPath]);

  // ✅ GPS: Check location on page load
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsStatus("unavailable");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsCoords({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setGpsStatus("ready");
      },
      (err) => {
        console.warn("GPS error:", err);
        setGpsStatus(err.code === 1 ? "denied" : "unavailable");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // ✅ Check NFC support
  useEffect(() => {
    setNfcSupported("NDEFReader" in window);
  }, []);

  // ✅ Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const goBack = () => navigate(-1);

  const clearMessage = useCallback(() => {
    setMessage("");
    setActivationResults(null);
  }, []);

  // ✅ GPS helper: get fresh coordinates
  const getGPSLocation = async (): Promise<{ latitude: number; longitude: number; accuracy?: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("Geolocation not supported by this browser"));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          setGpsCoords(coords);
          setGpsStatus("ready");
          resolve(coords);
        },
        (err) => {
          setGpsStatus(err.code === 1 ? "denied" : "unavailable");
          reject(new Error(
            err.code === 1
              ? "Location access denied. Please enable GPS in your browser settings to activate tags."
              : "Could not determine your location. Please try again."
          ));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  // Enhanced CSV content processing
  const processCSVContent = useCallback((content: string): string => {
    const lines = content.replace(/^\uFEFF/, '').split(/\r?\n/).filter(line => line.trim());
    
    if (lines.length === 0) return 'TagID\n';
    
    const firstLine = lines[0].trim();
    const possibleHeaders = /^(tagid|tag_id|id|code|tag)$/i;
    
    // If first line looks like data (long alphanumeric), prepend header
    if (firstLine.length > 10 && !possibleHeaders.test(firstLine)) {
      return `TagID\n${lines.join('\n')}`;
    }
    
    // If first line is a header but not TagID, replace it
    if (possibleHeaders.test(firstLine) && !/^tagid$/i.test(firstLine)) {
      lines[0] = 'TagID';
    }
    
    return lines.join('\n');
  }, []);

  // ✅ NFC Scan to get TagID
  const startNFCScan = async () => {
    if (!("NDEFReader" in window)) {
      setNfcStatus("NFC not supported on this browser/device");
      return;
    }
    try {
      setNfcScanning(true);
      setNfcStatus("Tap NFC chip to the back of your phone...");
      const ndef = new (window as any).NDEFReader();
      await ndef.scan();
      ndef.onreading = (event: any) => {
        const record = event.message.records[0];
        if (record.recordType === "url") {
          const decoder = new TextDecoder();
          const url = decoder.decode(record.data);
          const resolved = resolveTagId(url);
          setTagId(resolved);
          setNfcStatus(`✅ NFC scanned: ${resolved}`);
          setScanMode("manual");
        }
        setNfcScanning(false);
      };
      ndef.onreadingerror = () => {
        setNfcStatus("❌ Could not read NFC tag");
        setNfcScanning(false);
      };
    } catch (err: any) {
      setNfcStatus(`❌ NFC error: ${err.message}`);
      setNfcScanning(false);
    }
  };

  // ✅ Stop NFC scan
  const stopNFCScan = () => {
    setNfcScanning(false);
    setNfcStatus("");
    setScanMode("manual");
  };

  /** -------------------- Single Tag Scan -------------------- **/
  const activateTag = async () => {
    clearMessage();
    
    if (!tagId.trim()) {
      setMessage("Please enter a Tag ID.");
      return;
    }

    if (!contextComplete) {
      setMessage("Please fill in all context fields (Business ID, Product ID, Batch ID, Store ID).");
      return;
    }

    setScanLoading(true);
    try {
      const token = BusinessApiService.getBusinessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // ✅ GPS Security: Get fresh coordinates
      let geoLocation;
      try {
        geoLocation = await getGPSLocation();
      } catch (gpsErr: any) {
        setMessage(gpsErr.message);
        setScanLoading(false);
        return;
      }

      // Send activation request with GPS location
      const response = await fetch(
        `${API_BASE}/tags/business/scan-activate/${encodeURIComponent(
          tagId.trim().toUpperCase()
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            businessId,
            productId,
            batchId,
            storeId,
            activatedBy: "manual-scan",
            location: geoLocation,
            deviceInfo: {
              userAgent: navigator.userAgent,
              platform: navigator.platform,
            },
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success !== false) {
        setActivationResults({
          success: true,
          message: `Tag ${tagId.trim().toUpperCase()} activated successfully`,
          activated: 1,
          failed: 0,
          details: [
            `Tag linked to batch ${batchId}`,
            `Linked to business ${businessId}`,
            `Linked to store ${storeId}`,
            `Linked to product ${productId}`,
            ...(result.gpsValidation ? [`GPS verified: ${result.gpsValidation.distanceFromStore}m from store "${result.gpsValidation.storeName}"`] : [])
          ]
        });
        setTagId("");
      } else {
        setMessage(`Activation failed: ${result.error || result.message || `HTTP ${response.status}`}`);
      }
    } catch (error: any) {
      console.error("Scan activation error:", error);
      setMessage(`Network error: ${error?.message || "Unknown error occurred"}`);
    } finally {
      setScanLoading(false);
    }
  };

  /** -------------------- CSV Bulk Activate -------------------- **/
  const activateCSV = async () => {
    clearMessage();
    
    if (!csvData.trim() && !csvFile) {
      setMessage("Please paste CSV data or upload a CSV file.");
      return;
    }

    if (!contextComplete) {
      setMessage("Please fill in all context fields (Business ID, Product ID, Batch ID, Store ID).");
      return;
    }

    setLoading(true);
    try {
      const token = BusinessApiService.getBusinessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // ✅ GPS Security: Get fresh coordinates
      let geoLocation;
      try {
        geoLocation = await getGPSLocation();
      } catch (gpsErr: any) {
        setMessage(gpsErr.message);
        setLoading(false);
        return;
      }

      const formData = new FormData();
      
      let processedContent: string;
      let fileName: string;

      if (csvFile) {
        // Read and process the file to ensure proper format
        const fileContent = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string || "");
          reader.readAsText(csvFile);
        });
        
        processedContent = processCSVContent(fileContent);
        fileName = csvFile.name;
      } else {
        // Process pasted CSV data
        processedContent = processCSVContent(csvData);
        fileName = "tags.csv";
      }

      // Validate processed content
      const lines = processedContent.split(/\r?\n/).filter(line => line.trim());
      if (lines.length <= 1) { // Only header or empty
        setMessage("CSV appears to be empty or contains no valid tag data.");
        setLoading(false);
        return;
      }

      const blob = new Blob([processedContent], { type: "text/csv" });
      formData.append("file", blob, fileName);
      formData.append("businessId", businessId);
      formData.append("productId", productId);
      formData.append("batchId", batchId);
      formData.append("storeId", storeId);
      formData.append("activatedBy", "csv-upload");
      formData.append("location", JSON.stringify(geoLocation));

      console.log("Sending CSV activation request for batch:", batchId);

      const response = await fetch(
        `${API_BASE}/tags/business/activate-csv-with-batch`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();
      console.log("CSV activation response:", result);

      if (response.ok && result.success !== false) {
        const activated = result.activated || result.linked || result.summary?.tagsActivated || result.count || 0;
        const failed = result.failed || result.summary?.tagsFailed || 0;
        
        setActivationResults({
          success: true,
          message: `CSV processing complete: ${activated} tags activated`,
          activated,
          failed,
          details: [
            `${activated} tags linked to batch ${batchId}`,
            `All tags linked to business ${businessId}`,
            `All tags linked to store ${storeId}`,
            `All tags linked to product ${productId}`,
            ...(result.details || [])
          ],
          errors: result.errors || [],
          summary: result.summary,
          notFound: result.notFound
        });
        
        setCsvData("");
        setCsvFile(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        const errorMessage = result.error || result.message || `HTTP ${response.status}`;
        setMessage(`CSV activation failed: ${errorMessage}`);
        
        // Show additional details if available
        if (result.details) {
          console.error('Activation details:', result.details);
        }
      }
    } catch (error: any) {
      console.error("CSV activation error:", error);
      setMessage(`Network error: ${error?.message || "Unknown error occurred"}`);
    } finally {
      setLoading(false);
    }
  };

  /** -------------------- File Handling -------------------- **/
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setMessage("Please upload a CSV file.");
        return;
      }
      
      setCsvFile(file);
      setCsvData(""); // Clear paste data when file is uploaded
      clearMessage(); // Clear any previous messages
      
      // Preview and validate file content
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          const processedContent = processCSVContent(content);
          const lines = processedContent.split(/\r?\n/).filter(line => line.trim());
          
          console.log('CSV preview:', {
            totalLines: lines.length,
            firstFewLines: lines.slice(0, 5),
            hasHeader: /^tagid$/i.test(lines[0] || ''),
            sampleTags: lines.slice(1, 4)
          });
        }
      };
      reader.readAsText(file);
    }
  };

  const downloadSampleCSV = () => {
    const sampleContent = `TagID
ABC123XYZ789DEF456GHI012TEST1
ABC123XYZ789DEF456GHI012TEST2
ABC123XYZ789DEF456GHI012TEST3
ABC123XYZ789DEF456GHI012TEST4
ABC123XYZ789DEF456GHI012TEST5`;
    
    const blob = new Blob([sampleContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_tags.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Batches
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Activate Tags</h1>
        <div className="w-20"></div> {/* Spacer for centering */}
      </div>

      {/* ✅ GPS Status Banner */}
      <div className={`p-3 rounded-lg mb-4 flex items-center gap-2 text-sm ${
        gpsStatus === "ready"
          ? "bg-green-50 border border-green-200 text-green-700"
          : gpsStatus === "checking"
          ? "bg-blue-50 border border-blue-200 text-blue-700"
          : "bg-red-50 border border-red-200 text-red-700"
      }`}>
        {gpsStatus === "ready" && (
          <>
            <CheckCircle size={16} />
            GPS Active — Location verified ({gpsCoords?.latitude.toFixed(4)}, {gpsCoords?.longitude.toFixed(4)})
            {gpsCoords?.accuracy && ` · Accuracy: ${Math.round(gpsCoords.accuracy)}m`}
          </>
        )}
        {gpsStatus === "checking" && (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            Detecting GPS location...
          </>
        )}
        {gpsStatus === "denied" && (
          <>
            <AlertCircle size={16} />
            GPS access denied. Please enable location in your browser settings to activate tags.
          </>
        )}
        {gpsStatus === "unavailable" && (
          <>
            <AlertCircle size={16} />
            GPS unavailable. Location is required to activate tags at the store.
          </>
        )}
      </div>

      {/* Context Panel */}
      <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Activation Context</h3>
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business ID *</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={businessId}
              onChange={(e) => setBusinessId(e.target.value.trim())}
              placeholder="BIZ-XXXX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product ID *</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={productId}
              onChange={(e) => setProductId(e.target.value.trim())}
              placeholder="PROD-XXXX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Batch ID *</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value.trim())}
              placeholder="BATCH-XXXX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Store ID *</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value.trim())}
              placeholder="STORE-XXXX"
            />
          </div>
        </div>
        {!contextComplete && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-amber-600" />
              <p className="text-sm text-amber-700">
                All context fields are required. Tags will be linked to: Business → Store → Product → Batch
              </p>
            </div>
          </div>
        )}
        {contextComplete && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-600" />
              <p className="text-sm text-green-700">
                Context complete! Tags will be linked to batch <strong>{batchId}</strong>, 
                business <strong>{businessId}</strong>, store <strong>{storeId}</strong>, 
                and product <strong>{productId}</strong>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {activationResults && (
        <div className={`p-4 rounded-lg mb-6 ${
          activationResults.success
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          <div className="flex items-start gap-2">
            {activationResults.success ? (
              <CheckCircle size={20} className="text-green-600 mt-0.5" />
            ) : (
              <AlertCircle size={20} className="text-red-600 mt-0.5" />
            )}
            <div>
              <h4 className="font-semibold">{activationResults.message}</h4>
              {activationResults.activated !== undefined && (
                <p className="text-sm mt-1">
                  Successfully activated: {activationResults.activated} tags
                  {activationResults.failed ? ` | Failed: ${activationResults.failed} tags` : ""}
                </p>
              )}
             {activationResults.summary && (
  <div className="mt-2 text-sm">
    <p>Success Rate: {activationResults.summary.successRate || 'N/A'}</p>
    {(activationResults.summary.tagsNotFound ?? 0) > 0 && (
      <p>Tags not found: {activationResults.summary.tagsNotFound}</p>
    )}
  </div>
)}
              {activationResults.details && activationResults.details.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium">Details:</p>
                  <ul className="text-xs mt-1 space-y-1">
                    {activationResults.details.map((detail, idx) => (
                      <li key={idx}>• {detail}</li>
                    ))}
                  </ul>
                </div>
              )}
              {activationResults.errors && activationResults.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium">Errors:</p>
                  <ul className="text-xs mt-1 space-y-1">
                    {activationResults.errors.map((error, idx) => (
                      <li key={idx} className="text-red-700">• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {activationResults.notFound && activationResults.notFound.count > 0 && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-xs font-medium">
                    {activationResults.notFound.count} tags not found
                  </p>
                  <p className="text-xs text-yellow-700">{activationResults.notFound.reason}</p>
                  {activationResults.notFound.sample && (
                    <p className="text-xs font-mono mt-1">
                      Sample: {activationResults.notFound.sample.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {message && !activationResults && (
        <div className="p-4 rounded-lg mb-6 bg-red-50 text-red-800 border border-red-200">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} className="text-red-600" />
            {message}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Single Tag Scan */}
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Scan size={20} className="text-blue-600" />
            Scan Single Tag
          </h3>

          <div className="space-y-4">
            {/* ✅ Scan Mode Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setScanMode("manual")}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors flex items-center justify-center gap-1 ${scanMode === "manual" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
              >
                <Scan size={14} /> Manual
              </button>
              {nfcSupported && (
                <button
                  onClick={() => { setScanMode("nfc"); startNFCScan(); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors flex items-center justify-center gap-1 ${scanMode === "nfc" ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                >
                  <Wifi size={14} /> NFC Tap
                </button>
              )}
            </div>

            {/* NFC scanning status */}
            {nfcScanning && (
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg text-center">
                <div className="text-3xl mb-2">📡</div>
                <p className="text-purple-700 font-medium text-sm">{nfcStatus}</p>
                <button onClick={stopNFCScan} className="mt-2 text-xs text-purple-500 underline">Cancel</button>
              </div>
            )}
            {nfcStatus && !nfcScanning && (
              <div className={`p-2 rounded-lg text-sm text-center ${nfcStatus.includes("✅") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {nfcStatus}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tag ID {tagId && <span className="text-green-600 font-mono text-xs ml-2">{tagId}</span>}
              </label>
              <input
                type="text"
                value={tagId}
                onChange={(e) => setTagId(resolveTagId(e.target.value))}
                placeholder="Scan NFC / paste URL / type TagID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Supports: plain TagID, GS1 URL, NFC URL, ?uid= format
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GPS Location (Auto-detected)
              </label>
              <div className={`w-full px-3 py-2 rounded-lg text-sm ${
                gpsStatus === "ready"
                  ? "bg-green-50 border border-green-300 text-green-700"
                  : "bg-red-50 border border-red-300 text-red-700"
              }`}>
                {gpsStatus === "ready"
                  ? `Location verified — you must be at the store to activate.`
                  : gpsStatus === "checking"
                  ? "Detecting GPS..."
                  : "GPS required — enable location in browser settings."}
              </div>
            </div>

            <button
              onClick={activateTag}
              disabled={scanLoading || !tagId.trim() || !contextComplete || gpsStatus === "denied" || gpsStatus === "unavailable"}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {scanLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Activating...
                </>
              ) : (
                <>
                  <Scan size={16} />
                  Activate Tag & Link to All
                </>
              )}
            </button>
          </div>
        </div>

        {/* CSV Import */}
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Upload size={20} className="text-green-600" />
            Import CSV Tags
          </h3>

          <div className="space-y-4">
            {/* File Upload */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Upload CSV File
                </label>
                <button
                  onClick={downloadSampleCSV}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <Download size={12} />
                  Sample CSV
                </button>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {csvFile && (
                <p className="text-xs text-green-600 mt-1">
                  File loaded: {csvFile.name} ({Math.round(csvFile.size / 1024)}KB)
                </p>
              )}
            </div>

            <div className="text-center text-gray-500 text-sm">- OR -</div>

            {/* Paste CSV */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Paste CSV Data
              </label>
              <textarea
                value={csvData}
                onChange={(e) => {
                  setCsvData(e.target.value);
                  setCsvFile(null); // Clear file when pasting
                  clearMessage(); // Clear any previous messages
                }}
                placeholder="TagID&#10;ABC123XYZ789DEF456GHI012TEST1&#10;ABC123XYZ789DEF456GHI012TEST2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                rows={6}
                disabled={!!csvFile}
              />
              <p className="text-xs text-gray-500 mt-1">
                Header is optional; will be added automatically if missing.
              </p>
            </div>

            <button
              onClick={activateCSV}
              disabled={loading || (!csvData.trim() && !csvFile) || !contextComplete || gpsStatus === "denied" || gpsStatus === "unavailable"}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing CSV...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Activate CSV & Link to All
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-blue-900 mb-3">Tag Linking Process:</h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <h5 className="font-medium mb-2">Single Tag Activation:</h5>
            <ul className="space-y-1">
              <li>• Enter the complete Tag ID</li>
              <li>• GPS verifies you are at the store location</li>
              <li>• Tag will be linked to Business, Store, Product & Batch</li>
              <li>• Enables consumer scanning and verification</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium mb-2">CSV Bulk Activation:</h5>
            <ul className="space-y-1">
              <li>• CSV must have TagID column (header optional)</li>
              <li>• GPS verifies you are at the store location</li>
              <li>• All tags linked to same Business/Store/Product/Batch</li>
              <li>• Download sample CSV for reference format</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-blue-100 rounded-lg">
          <h5 className="font-medium text-blue-900 mb-2">What happens when tags are activated:</h5>
          <div className="text-sm text-blue-800 space-y-1">
            <p>1. GPS verifies you are at the store location</p>
            <p>2. Tag status changes from "imported" to "activated"</p>
            <p>3. Tag is linked to your business ({businessId || 'BIZ-XXXX'})</p>
            <p>4. Tag is linked to the store ({storeId || 'STORE-XXXX'})</p>
            <p>5. Tag is linked to the product ({productId || 'PROD-XXXX'})</p>
            <p>6. Tag is linked to the batch ({batchId || 'BATCH-XXXX'})</p>
            <p>7. Consumers can now scan and verify these tags</p>
          </div>
        </div>
      </div>
    </div>
  );
}
